import { create } from 'zustand';
import {
  setBillingBlockHandler,
  setBillingBlockState,
} from '../services/api';

/**
 * Tipo de bloqueio de acesso (Epic #884 §3.1):
 * - `paywall` → HTTP 402 `BILLING_*` (cliente/tenant sem pagamento). Mostra
 *   CTA de cartão.
 * - `org_suspended` → HTTP 403 `ORG_SUSPENDED` (white-label indisponível). Tela
 *   neutra, SEM CTA de pagamento (pagar não resolve).
 */
export type BillingBlockKind = 'paywall' | 'org_suspended';

export interface BillingBlockState {
  blocked: boolean;
  kind: BillingBlockKind | null;
  /** Código retornado pelo backend (`BILLING_TRIAL_EXPIRED`, `ORG_SUSPENDED`, ...). */
  code: string | null;
  message: string | null;

  block: (input: {
    kind: BillingBlockKind;
    code?: string | null;
    message?: string | null;
  }) => void;
  clear: () => void;
}

/**
 * Billing Block Store (Zustand)
 *
 * Estado global de bloqueio de acesso por billing. Populado pelo interceptor
 * Axios (`api.ts`) ao receber 402/403-ORG_SUSPENDED, e consumido pelo gate de
 * rotas em `App.tsx` para renderizar a `PaywallScreen` ou a
 * `AccountUnavailableScreen`.
 *
 * Não persistido — é estado derivado das respostas da API (revalidado a cada
 * request).
 */
export const useBillingBlockStore = create<BillingBlockState>((set) => ({
  blocked: false,
  kind: null,
  code: null,
  message: null,

  block: ({ kind, code, message }) => {
    set({ blocked: true, kind, code: code ?? null, message: message ?? null });
    // Mirror into api.ts so the REQUEST interceptor short-circuits every
    // non-allowlisted request while blocked (store is the source of truth).
    setBillingBlockState({ kind, code: code ?? null, message: message ?? null });
  },
  clear: () => {
    set({ blocked: false, kind: null, code: null, message: null });
    setBillingBlockState(null);
  },
}));

// Bridge: let the Axios interceptor (which must not import this store directly,
// to avoid a circular dependency) push billing-block events into the store.
setBillingBlockHandler((event) => {
  if (event === null) {
    useBillingBlockStore.getState().clear();
    return;
  }
  useBillingBlockStore.getState().block(event);
});
